import { useState } from "react";
import { AppDialog } from "../../../../../common/components/AppDialog/AppDialog";
import { RenderWhen } from "../../../../../common/infrastructure/components/RenderWhen";
import { IUserModel } from "../models/userModel";
import { AppTooltip } from "../../../../../common/components/tooltip/AppTooltip";
import { classNames } from "primereact/utils";
import { useUserAdditionalDetailsDialog } from "../hooks/useUserAdditionalDetailsDialog";
import { ConsentPolicyType } from "../../../../../common/enums";
import { dateUtility } from "../../../../../common/infrastructure/utilities/dateUtility";
import parentStyles from "../UserAdministrationUser.module.scss";
import styles from "./UserAdditionalDetailsDialog.module.scss";
import { useAppContext } from "../../../../../AppContext";
import { isStoreOnlyStudio } from "../../../../../common/infrastructure/helpers/studioHelper";
import { PriceDisplay } from "../../../../../common/components/PriceDisplay/PriceDisplay";
import { SectionInfoRow } from "../../../../../common/components/common/SectionInfoRow";
import { useApiMutation } from "../../../../../common/infrastructure/hooks/useApiMutation";
import { endpointsMetadata } from "../../../../../common/infrastructure/routes/endpointsMetadata";
import { ConfirmButton } from "../../../../../common/components/button/confirmButton/ConfirmButton";

interface IUserAdditionalDetailsDialogProps {
  visible: boolean;
  onHide: () => void;
  user?: IUserModel;
}

export const UserAdditionalDetailsDialog = ({
  visible,
  onHide,
  user,
}: IUserAdditionalDetailsDialogProps) => {
  const { getScheduleVisibilityTooltip, formatScheduleVisibilityValue } =
    useUserAdditionalDetailsDialog(user);

  const {
    showGroupTypeOptions,
    showServicesTypeOptions,
    showGymTypeOptions,
    selectedStudio,
  } = useAppContext();
  const isGymOnly =
    showGymTypeOptions && !showGroupTypeOptions && !showServicesTypeOptions;
  // A shop-only studio has no classes, so only the generated-turnover stat is relevant.
  const isStoreOnly = isStoreOnlyStudio(selectedStudio);
  const [isEmailConfirmed, setIsEmailConfirmed] = useState<boolean | undefined>(undefined);
  const { mutateAsync: confirmEmailMutate, isLoading: isConfirming } = useApiMutation();

  const emailConfirmed = isEmailConfirmed ?? user?.emailConfirmed;

  const handleConfirmEmail = async () => {
    if (!user?.id || isConfirming) return;
    try {
      await confirmEmailMutate({
        endpoint: endpointsMetadata.user.confirmUserEmail,
        method: "put",
        disableSuccessToast: false,
        globalIsLoading: false,
        queryParams: { userId: user.id },
      });
      setIsEmailConfirmed(true);
    } catch {}
  };

  const getConsentLabel = (type: ConsentPolicyType): string => {
    switch (type) {
      case ConsentPolicyType.PlannyPrivacyPolicy:
        return "Политика за поверителност";
      case ConsentPolicyType.AutomatedEmails:
        return "Автоматизирани имейли";
      case ConsentPolicyType.MarketingEmail:
        return "Маркетинг имейли";
      case ConsentPolicyType.TenantTermsOfUse:
        return "Общи условия за ползване";
      default:
        return "";
    }
  };

  const renderConsentValue = (givenAt?: Date, isOutdated?: boolean) => {
    // If no consent given at all (no date), show X
    if (!givenAt) {
      return <span>X</span>;
    }

    // If consent was given, show date and warning if outdated
    return (
      <div className={styles.consent_value}>
        {!isOutdated && (
          <>
            <span>✓</span>
            <span className={styles.consent_date}>
              ({dateUtility.formatDate(givenAt)})
            </span>
          </>
        )}
        {isOutdated && (
          <span className={styles.consent_outdated_warning}>
            <AppTooltip
              content={`Съгласието е дадено за по-стара версия на политиката. Дата, на която е дадено съгласието: ${dateUtility.formatDate(
                givenAt
              )}`}
            />
          </span>
        )}
      </div>
    );
  };

  return (
    <AppDialog
      header="Допълнителни детайли"
      visible={visible}
      className={parentStyles.root_additional_details_dialog}
      onHide={onHide}
    >
      <div className={parentStyles.root_additional_details_dialog_content}>
        {/* Statistics Section */}
        <RenderWhen condition={!!user?.canSeeUserStatistics}>
          <div>
            <p className={styles.category_header}>Статистики</p>

            <RenderWhen condition={!isGymOnly && !isStoreOnly}>
              <SectionInfoRow
                label="Общо записани класове"
                data={`${user?.totalClasses}бр.`}
                isLoading={false}
              />

              <SectionInfoRow
                label="Общо взети класове"
                data={`${user?.totalClassesTaken}бр.`}
                isLoading={false}
              />

              <SectionInfoRow
                label="Общо пропуснати класове (неплатени)"
                data={`${user?.totalClassesMissedUnpaid}бр.`}
                isLoading={false}
              />

              <SectionInfoRow
                label="Общо пропуснати класове (платени)"
                data={`${user?.totalClassesMissedPaid}бр.`}
                isLoading={false}
              />

              <SectionInfoRow
                label="Общо отказани класове"
                data={`${user?.totalClassesCancelled}бр.`}
                isLoading={false}
              />
            </RenderWhen>

            <SectionInfoRow
              label="Общо генериран оборот"
              data={<PriceDisplay minPrice={user?.totalIncomeGeneratedInEur ?? 0} />}
              isLoading={false}
            />
          </div>
        </RenderWhen>

        {/* Consents Section */}
        <div
          className={classNames({
            [styles.category_separator]: user?.canSeeUserStatistics,
            [styles.category_no_separator]: !user?.canSeeUserStatistics,
          })}
        >
          <p className={styles.category_header}>Съгласия</p>

          {user?.consents?.map((consent) => (
            <SectionInfoRow
              key={consent.type}
              label={getConsentLabel(consent.type)}
              data={renderConsentValue(consent.givenAt, consent.isOutdated)}
              isLoading={false}
            />
          ))}
        </div>

        {/* Others Section */}
        <div className={styles.category_separator}>
          <p className={styles.category_header}>Други</p>

          <RenderWhen condition={showGroupTypeOptions}>
            <SectionInfoRow
              label="Видимост на графика"
              data={
                <span className={styles.value_with_tooltip}>
                  {formatScheduleVisibilityValue()}
                  {!user?.scheduleVisibilityDaysAhead && (
                    <AppTooltip content={getScheduleVisibilityTooltip()} />
                  )}
                </span>
              }
              isLoading={false}
            />
          </RenderWhen>

          <SectionInfoRow
            label="Потвърден имейл"
            data={
              emailConfirmed ? (
                <span>✓</span>
              ) : (
                <div className={styles.email_confirm_row}>
                  <span>X</span>
                  {user?.email && (
                    <ConfirmButton
                      onClick={handleConfirmEmail}
                      loading={isConfirming}
                      title="Потвърди имейла ръчно"
                    />
                  )}
                </div>
              )
            }
            isLoading={false}
          />

          <SectionInfoRow
            label="Дата на раждане"
            data={
              user?.birthDate
                ? dateUtility.formatDate(new Date(user.birthDate))
                : "N/A"
            }
            isLoading={false}
          />
        </div>
      </div>
    </AppDialog>
  );
};
